import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { Schema } from 'effect';
import { Effect } from 'effect';

import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { Paths } from '@vs-shop/schemas/openapi/paths';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/customers/{id}';
const HTTP_METHOD = 'put';

export type CustomerUpdateRequestBody = Paths[typeof ENDPOINT][typeof HTTP_METHOD] extends {
  requestBody: { content: infer Content };
}
  ? Content extends Record<string, infer Body>
    ? Body
    : never
  : never;

export type MakeUpdateCustomerDeps<CustomerShowSchema extends Schema.Top, CustomerUpdateInput> = {
  readonly customerShow: CustomerShowSchema;
  readonly customerShowEmail: (customer: CustomerShowSchema['Type']) => string;
  readonly customerUpdateBody: (data: CustomerUpdateInput) => CustomerUpdateRequestBody;
};

export const makeUpdateCustomer = <CustomerShowSchema extends Schema.Top, CustomerUpdateInput>(
  deps: MakeUpdateCustomerDeps<CustomerShowSchema, CustomerUpdateInput>
) => {
  const UpdateCustomerError = createBackendApiError({
    domain: 'Customer',
    endpoint: 'Update',
    path: ENDPOINT,
    identifierField: 'customerId',
  });

  const putCustomerFetch = (id: string, data: CustomerUpdateInput) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;
      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json', 'application/json')
        .fetch({ params: { id }, body: deps.customerUpdateBody(data) });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'customer.id': id,
        },
      })
    );

  const updateCustomer = (id: string, data: CustomerUpdateInput) =>
    putCustomerFetch(id, data).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.flatMap(
        HttpClientResponse.matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.customerShow)(response),
          404: (response) =>
            Effect.fail(new UpdateCustomerError.NotFoundError({ customerId: id, response })),
          422: UpdateCustomerError.createViolationError(id),
          orElse: (response) =>
            Effect.fail(new UpdateCustomerError.ApiRequestError({ customerId: id, response })),
        })
      ),
      Effect.tap((result) =>
        logApiOperation(
          ApiModule.CustomerUpdate,
          'Debug',
          `Successfully updated customer ${id} -> email: ${deps.customerShowEmail(result)}`
        )
      ),
      Effect.withSpan('api.customer.update', {
        kind: 'internal',
        attributes: {
          'customer.id': id,
        },
      })
    );

  return { UpdateCustomerError, updateCustomer };
};
